import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Gift, Sparkles, FileText, LayoutGrid, Hash,
  Flame, Crown, Check, Quote, Zap, Users, Clock,
} from "lucide-react";
import { startCheckout } from "@/lib/checkout";
import { useSeo } from "@/lib/useSeo";

const LOGO = "/assets/logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HOOK_CATEGORIES = [
  {
    no: "01",
    title: "Pattern Interrupts & Scroll-Stoppers",
    when: "Top-of-funnel awareness on TikTok, Reels & Shorts. Break the passive scroll in the first 1–2 seconds.",
    example: "Stop scrolling if you actually want to [desired outcome].",
  },
  {
    no: "02",
    title: "Curiosity Gap & Intrigue",
    when: "When you want long watch times and high retention. Open a loop the viewer can't leave unclosed.",
    example: "The real reason [common belief] keeps failing you…",
  },
  {
    no: "03",
    title: "Pain Point & Problem-Solving",
    when: "Mid/bottom-of-funnel education, lead-gen & product sales. Build instant empathy and authority.",
    example: "Tired of dealing with [pain point]? Do this instead.",
  },
  {
    no: "04",
    title: "Value-Packed Educational Listicles",
    when: "Save/bookmark & carousel campaigns. Numbers lower friction and promise structured value.",
    example: "5 free tools that feel illegal to know about.",
  },
  {
    no: "05",
    title: "Personal Storytelling & Transformation",
    when: "Personal branding & founder authority. Trigger emotional resonance that turns cold leads into fans.",
    example: "How I went from rock bottom to [result] in [timeframe].",
  },
  {
    no: "06",
    title: "High-Converting CTA & Engagement",
    when: "When your goal is comments, shares & saves — the exact signals the algorithm rewards.",
    example: "Comment [Keyword] and I'll send you the full free guide.",
  },
  {
    no: "07",
    title: "Direct Address & Niche Targeting",
    when: "Targeted ads & niche products. Make the right viewer instantly self-identify and pay attention.",
    example: "Calling all [target audience] — you need to see this.",
  },
];

const REEL_BONUSES = [
  {
    tag: "Bonus 02",
    icon: Flame,
    title: "100 Hustle Mindset Reels",
    img: "/assets/bonus-hustle-reels.jpg",
    alt: "100 Hustle Mindset Reels bonus — ready-to-post faceless motivational Instagram Reels editable in Canva with MRR & PLR",
    blurb:
      "A vault of 100 scroll-stopping motivational reels — clean typography, cinematic B-roll, and hard-hitting hustle overlays. Post-ready and fully editable.",
    benefits: [
      "Post daily without ever filming or editing",
      "Grow a faceless motivation / entrepreneur page fast",
      "Fully rebrandable — MRR & PLR rights included",
    ],
  },
  {
    tag: "Bonus 03",
    icon: Crown,
    title: "200 Luxe Life Reels",
    img: "/assets/bonus-luxe-woman.jpg",
    alt: "200 Luxe Life Reels bonus — aesthetic faceless luxury lifestyle videos with motivational overlays for an aspirational brand",
    blurb:
      "Over 200 aesthetic luxury-lifestyle reels with aspirational overlays — cars, travel, wealth, and elegance — engineered to build an elite, high-status brand.",
    benefits: [
      "Trigger aspiration & desire — the emotions that sell",
      "Position your page as premium and high-status",
      "Two months of daily content, ready in one download",
    ],
  },
];

const HOOK_SHIP = [
  { step: "01", title: "Pick the post you're stuck on", desc: "Open the PDF before you write. Jump to the category that matches the job this post is doing." },
  { step: "02", title: "Replace one opener", desc: "You don't need all 350. Drop a single hook into your draft and read the first line out loud." },
  { step: "03", title: "Ship it", desc: "A divider system lets you return to the same category on the next post — and the one after that." },
];

function BonusHeader() {
  return (
    <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-black/70 border-b border-white/5">
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="bonus-home-link">
          <img src={LOGO} alt="Social Media Combo logo" className="w-8 h-8 object-contain" />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Social Media Combo</span>
            <span className="font-serif-display text-base tracking-[0.25em] font-semibold">
              THE FACELESS BUNDLE<span className="text-[#D4AF37]">.</span>
            </span>
          </div>
        </Link>
        <button
          onClick={startCheckout}
          data-testid="bonus-header-cta"
          className="hidden sm:inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-black transition-colors"
        >
          Get Access — $27
        </button>
      </div>
    </header>
  );
}

export default function Bonuses() {
  useSeo({
    title: "Free Bonuses — 350 Viral Hooks + 300 Faceless Reels | The Faceless Bundle",
    description:
      "Get 350 winning hooks, 100 hustle-mindset reels and 200 luxe-life reels free with The Faceless Bundle — every scroll-stopper and ready-to-post reel you need to grow a faceless brand.",
    path: "/bonuses",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <BonusHeader />

      {/* ===== Hero ===== */}
      <section className="relative pt-36 pb-24 grain" data-testid="bonus-hero">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[700px] bg-[#D4AF37]/8 blur-[140px] rounded-full pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 text-center">
          <motion.div
            initial="hidden" animate="show" variants={fadeUp}
            className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-5 py-2 mb-8"
          >
            <Gift className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Included free with the bundle</span>
          </motion.div>

          <motion.h1
            initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="font-serif-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight"
          >
            Three elite vaults.<br />
            <span className="gold-shimmer">Yours free</span> with the bundle.
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-8 text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-2xl mx-auto"
          >
            When you grab The Faceless Bundle you also unlock <span className="text-white font-semibold">350 winning hooks</span>,
            <span className="text-white font-semibold"> 100 hustle-mindset reels</span> and
            <span className="text-white font-semibold"> 200 luxe-life reels</span> — every scroll-stopper and post you need to ship for months.
          </motion.p>

          <motion.div
            initial="hidden" animate="show" custom={3} variants={fadeUp}
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6"
          >
            <button onClick={startCheckout} data-testid="bonus-hero-cta" className="btn-gold inline-flex items-center gap-3 px-10 py-5 text-base sm:text-lg">
              Claim The Bundle + Bonuses <ArrowRight className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 text-sm text-zinc-400">
              <span className="font-serif-display text-2xl font-bold line-through text-zinc-600">$91</span>
              <span className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider">Free today</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== Bonus 01 — 350 Hooks deep dive ===== */}
      <section className="relative py-20 md:py-28 bg-[#080808] border-y border-[#D4AF37]/15" data-testid="bonus-hooks-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          {/* Intro */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
              <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Bonus 01 · The main event</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
                350 Winning Hooks, filed by purpose
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed">
                A polished PDF with <span className="text-white font-semibold">50 hand-written hooks</span> in each of
                seven practical categories — the warm-up set for every Reel, TikTok, carousel, short-form video and ad you ship from now on.
              </p>
              <p className="mt-4 text-zinc-400 leading-relaxed">
                A hook doesn't need to be clever — it needs to keep a viewer on the post long enough for the rest of your
                message to land. That's the entire job of the first sentence, and it's the hardest one in the room.
                These aren't AI mush; every opener is written to be the first line of a <em>real</em> post.
              </p>

              {/* Stats */}
              <div className="mt-8 grid grid-cols-3 divide-x divide-zinc-800 border border-zinc-800 bg-[#0A0A0A]">
                {[
                  { icon: Hash, label: "Hooks", val: "350" },
                  { icon: LayoutGrid, label: "Categories", val: "7 × 50" },
                  { icon: FileText, label: "Format", val: "PDF" },
                ].map((s, i) => (
                  <div key={i} className="p-5 text-center">
                    <s.icon className="w-5 h-5 text-[#D4AF37] mx-auto" />
                    <div className="mt-2 font-serif-display text-2xl font-bold">{s.val}</div>
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="absolute inset-0 bg-[#D4AF37]/10 blur-[80px] rounded-full" />
              <div className="relative border border-zinc-800 bg-[#111111] aspect-[4/3] overflow-hidden flex items-center justify-center">
                <img
                  src="/assets/bonus-350-hooks.jpg"
                  alt="350 Viral Hooks PDF — a master collection of winning social media hooks sorted into 7 categories"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>

          {/* Category grid */}
          <div className="mt-20">
            <motion.h3
              initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="font-serif-display text-2xl sm:text-3xl font-semibold text-center"
            >
              Seven categories, one job per page
            </motion.h3>
            <p className="mt-3 text-center text-zinc-500 max-w-2xl mx-auto">
              Open the divider that matches the job your post is doing, pick a hook, and replace the first line of your draft.
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
              {HOOK_CATEGORIES.map((c, i) => (
                <motion.div
                  key={c.no}
                  initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
                  custom={i % 2} variants={fadeUp}
                  data-testid={`hook-category-${i}`}
                  className="group relative bg-[#111111] border border-zinc-800 hover:border-[#D4AF37]/50 transition-colors p-7"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="font-serif-display text-4xl font-bold text-[#D4AF37]/25 group-hover:text-[#D4AF37]/50 transition-colors">
                        {c.no}
                      </span>
                      <h4 className="mt-2 font-serif-display text-xl font-semibold leading-snug">{c.title}</h4>
                    </div>
                    <span className="shrink-0 text-xs font-bold text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1">50</span>
                  </div>
                  <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{c.when}</p>
                  <div className="mt-5 flex items-start gap-2 border-l-2 border-[#D4AF37]/60 pl-4">
                    <Quote className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
                    <p className="text-sm italic text-zinc-300">"{c.example}"</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* How it ships */}
          <div className="mt-20">
            <motion.h3
              initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="font-serif-display text-2xl sm:text-3xl font-semibold text-center"
            >
              Three steps. One open PDF. No new tool to learn.
            </motion.h3>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {HOOK_SHIP.map((s, i) => (
                <motion.div
                  key={s.step}
                  initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                  className="relative border border-zinc-800 bg-[#0A0A0A] p-8"
                >
                  <span className="text-xs uppercase tracking-widest text-zinc-600">Step {s.step} / 03</span>
                  <h4 className="mt-3 font-serif-display text-xl font-semibold">{s.title}</h4>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* For the operator */}
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="mt-14 border border-[#D4AF37]/25 bg-gradient-to-r from-[#D4AF37]/5 via-transparent to-[#D4AF37]/5 p-8 md:p-10"
          >
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Users className="w-4 h-4" />
              <span className="text-xs uppercase tracking-[0.25em]">For the operator</span>
            </div>
            <p className="mt-4 font-serif-display text-xl md:text-2xl leading-relaxed max-w-3xl">
              Built for the people who already know the first sentence is the hardest one — creators posting every other day,
              social managers running four accounts, coaches whose Reels do the work a sales call used to, and small brands shipping
              paid content for the first time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== Bonus 02 & 03 — Reels ===== */}
      <section className="relative py-20 md:py-28" data-testid="bonus-reels-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Plus two done-for-you reel vaults</span>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              300 faceless reels, ready to post
            </h2>
            <p className="mt-5 text-zinc-400">
              Stop staring at a blank feed. These two vaults hand you months of high-performing, fully-rebrandable content.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {REEL_BONUSES.map((b, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fadeUp}
                data-testid={`reel-bonus-${i}`}
                className="group bg-[#111111] border border-zinc-800 hover:border-[#D4AF37]/60 transition-colors overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#1C1C1E]">
                  <img src={b.img} alt={b.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-black/30 pointer-events-none" />
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-[#D4AF37] text-black text-xs font-extrabold uppercase tracking-widest px-3 py-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Free
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                    <b.icon className="w-4 h-4" /> {b.tag}
                  </div>
                  <h3 className="mt-3 font-serif-display text-2xl font-semibold">{b.title}</h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{b.blurb}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {b.benefits.map((ben, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" /> {ben}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="relative py-24 md:py-32 bg-[#080808] border-t border-[#D4AF37]/20 grain overflow-hidden" data-testid="bonus-final-cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#D4AF37]/8 blur-[130px] rounded-full pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            All 11 ebooks + these 3 bonuses. <span className="gold-shimmer">$27.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
            The bonuses are sold separately for $91 — but they're included free when you grab the bundle today.
            Prices go up once the free bonuses are pulled.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-zinc-400">
            <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#D4AF37]" /> Instant access</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> Ready-to-post</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#D4AF37]" /> Lifetime license</span>
          </div>

          <button onClick={startCheckout} data-testid="bonus-final-cta-button" className="btn-gold mt-10 inline-flex items-center gap-3 px-10 py-5 text-base sm:text-lg">
            Claim The Bundle + 3 Free Bonuses <ArrowRight className="w-5 h-5" />
          </button>

          <div className="mt-10">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors" data-testid="bonus-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to store
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-zinc-900 py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-600">
          <span className="font-serif-display tracking-widest">SOCIAL MEDIA COMBO.</span>
          <div className="flex items-center gap-6">
            <Link to="/blog" className="hover:text-[#D4AF37] transition-colors">Blog</Link>
            <Link to="/bonuses" className="hover:text-[#D4AF37] transition-colors">Bonus</Link>
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
